import { ConfirmationError, Maybe, ResendCodeStatus, themes, Patient, utils } from 'kb-shared';
import React, { ChangeEvent, ReactNode, SyntheticEvent } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import Honeybadger from '@honeybadger-io/js';

import * as redux from '../redux';
import { ReduxState } from '../redux';
import { showErrorToast } from '../utilities/notificationUtils';
import Button from './Button/NewButton';
import TextInput from './TextInput/NewTextInput';

const confirmUser = redux.patient.confirmUser;
const resendConfirmationCode = redux.patient.resendConfirmationCode;
const { isEmailCodeValid } = utils;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const InstructionText = styled.div`
  ${themes.fonts.FoundersGrotesk};
  font-size: 20px;
  color: ${themes.colors.KBNight};
  text-align: center;
  padding-bottom: 12px;
`;

const DetailsText = styled.div`
  ${themes.fonts.FoundersGrotesk};
  font-size: 12px;
  color: ${themes.colors.KBNight};
  font-style: italic;
  text-align: center;
  padding-bottom: 48px;
  max-width: 200px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: 48px;
  padding-bottom: 40px;
`;

const ResendButton = styled.div`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  font-weight: bold;
  color: ${themes.colors.KBNight};
  text-decoration: underline;
  cursor: pointer;
`;

type Props = RouteComponentProps & {
  onError: (arg0: string) => void;
  onSuccess?: Function;
  patient: Patient;
  email?: Maybe<string>;
  confirmUser: (code: string, email?: string) => void;
  resendConfirmationCode: (email: string) => void;
  userConfirmationState: string | null;
  confirmationError: ConfirmationError | null;
  loading?: boolean;
  resendCode: ResendCodeStatus | null;
  location?: { state?: { email?: string } };
};

type State = {
  code: string;
  error: boolean;
};

class VerificationCode extends React.Component<Props, State> {
  static defaultProps = {
    onError: showErrorToast
  };

  state = {
    code: '',
    error: false
  };

  componentDidMount() {
    if (window.analytics) {
      window.analytics.page('Verification');
    }
  }

  UNSAFE_componentWillReceiveProps(newProps: Props) {
    if (newProps.userConfirmationState === 'confirmed') {
      if (this.props.userConfirmationState !== newProps.userConfirmationState) {
        if (this.props.onSuccess) {
          this.props.onSuccess();
        } else {
          this.props.history.replace('/login');
        }
        return;
      }
    }
    if (newProps.confirmationError && newProps.confirmationError !== this.props.confirmationError) {
      switch (newProps.confirmationError.type) {
        case 'Unknown':
        default:
          this.props.onError(
            "We're sorry, something went wrong. Please check your code and try again."
          );
          break;
        case 'InvalidCode':
          this.props.onError("Your code doesn't match. Please check again.");
          break;
        case 'ExpiredCode':
          this.props.onError(
            'Your code has expired. Please click the link below to send a new code.'
          );
          break;
      }
    }
    if (
      // @ts-ignore
      newProps.resendCode.status &&
      // @ts-ignore
      newProps.resendCode.status === 'error' &&
      // @ts-ignore
      newProps.resendCode.status !== this.props.resendCode.status
    ) {
      this.props.onError(
        "We're sorry, we were unable to resend your code. Please check your email address and try again."
      );
    }
  }

  _submitVerificationCode = (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    this.setState(() => ({ error: false }));

    const invalidCode = this._codeValidationError();
    if (invalidCode) {
      this.setState(() => ({
        error: true
      }));
      return this.props.onError(invalidCode);
    }

    // Send confirmation code to server
    this.props.confirmUser(this.state.code, this.getEmail());

    if (this.forgotPassword()) {
      this.props.history.replace('/forgot-password', { email: this.getEmail() });
    }
  };

  _codeValidationError(): string | null {
    if (isEmailCodeValid(this.state.code)) return null;
    return 'Your verification code should be six digits.';
  }

  updateCode = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      code: event.currentTarget.value
    });
  };

  renderResend(): ReactNode {
    const email = this.getEmail();
    let text = 'Resend code';
    // @ts-ignore
    if (this.props.resendCode.status === 'loading') {
      text = 'Resending code';
      // @ts-ignore
    } else if (this.props.resendCode.status === 'success') {
      text = 'Please check your email for a new code';
    }

    return (
      <ResendButton
        onClick={() => {
          this.props.resendConfirmationCode(email);
        }}
      >
        {text}
      </ResendButton>
    );
  }

  getEmail = (): string => {
    try {
      return (
        this.props.email ?? this.props.patient?.email ?? this.props.location?.state?.email ?? ''
      );
    } catch (error) {
      Honeybadger.notify(error);

      // this happens if the verification code page is loaded directly
      // because there is no email address in the state
      // force them to start over
      this.props.history.replace('/');
      return '';
    }
  };

  forgotPassword = (): boolean => {
    try {
      // @ts-ignore
      return this.props.forgotPassword || this.props.location.state.forgotPassword;
    } catch {
      return false;
    }
  };

  render() {
    const { code, error } = this.state;
    const { loading } = this.props;
    const email = this.getEmail();

    return (
      <Container>
        <Form onSubmit={this._submitVerificationCode}>
          <InstructionText>A verification code was sent to {email}</InstructionText>
          <DetailsText>It may take several minutes to receive this verification code.</DetailsText>
          <TextInput
            name={'Verification code'}
            label={'VERIFICATON CODE'}
            value={code}
            onChange={this.updateCode}
            error={error}
            spellCheck="false"
            placeholder={'Enter emailed code'}
            inputMode="numeric"
            pattern="[0-9]*"
          />
          <ButtonWrapper>
            <Button loading={loading} text={'SUBMIT VERIFICATION CODE'} type="submit" />
          </ButtonWrapper>
          {this.renderResend()}
        </Form>
      </Container>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  userConfirmationState: state.patient.confirmationState,
  patient: state.patient.patient,
  confirmationError: state.patient.confirmationError,
  resendCode: state.patient.resendConfirmationCode
});

const mapDispatchToProps = (dispatch: Function) => ({
  confirmUser: (code: string, email?: string) => dispatch(confirmUser(code, email)),
  resendConfirmationCode: (email: string) => dispatch(resendConfirmationCode(email))
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(VerificationCode));
